import { Plugin, Chart as ChartJs } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import history from 'history.js';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Card, CardHeader, CardBody, CardTitle } from 'reactstrap';

import { ChartColorMode, getChartData } from './helper';
import {
  BAR_CHART_OPTIONS,
  DOUGHNUT_CHART_OPTIONS,
  STACKED_BAR_CHART_OPTIONS,
} from './options';
import { avgArrays, mergeArrays } from '../../helper';
import { ChartType } from '../../types';
import 'views/Admin/Dashboard/styles.css';

type Props = {
  chartType: ChartType;
  title: string;
  labels: string[];
  tooltipLabels: string[];
  datasets: number[][];
  severityFilters: string[];
  customChartHeader?: JSX.Element;
  colorMode?: ChartColorMode;
  total_count?: number;
  /**
   * The label string shown along y axes for bar charts
   */
  yAxesLabel?: string;
  /**
   * Optional: only used for vuln metrics, for vuln counts display in tooltips
   */
  colors?: string[];
  tooltipsCallbackFunction?: (item, data) => any;
  tooltipsTitleCallbackFunction?: (item, data) => any;
  operationType?: string;
  getDrillDownUrl: (label: string, sourceType: string) => string;
  countDatasets?: number[][];
  setCurrentVulnCount?: (setCurrentVulnCount) => void;
};

// Explicitly cast ChartDataLabels to the correct type
const DoughnutChartDataLabels = ChartDataLabels as Plugin<'doughnut'>;

const BarOrDoughnutChart = ({
  chartType,
  title,
  labels,
  tooltipLabels,
  datasets,
  severityFilters,
  customChartHeader,
  colorMode,
  yAxesLabel,
  getDrillDownUrl,
  tooltipsCallbackFunction,
  tooltipsTitleCallbackFunction,
  operationType,
  countDatasets,
  total_count,
  setCurrentVulnCount,
  colors,
}: Props) => {
  const [hiddenIndexes, setHiddenIndexes] = useState<number[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [hiddenLabels, setHiddenLabels] = useState([]);
  const [redirectUrl, setRedirectUrl] = useState('');

  let barChartReference = useRef<any>();
  let pieChartReference = useRef<any>();

  // TO exclude the counts for hidden legends
  // we need to manipulate the chart datasets
  if (setCurrentVulnCount) {
    setCurrentVulnCount(totalCount.toString());
  }
  const calculatedDatasets: number[][] = useMemo(() => {
    const clonedDatasets = _.cloneDeep(datasets);

    if (chartType === ChartType.Doughnut && hiddenIndexes.length > 0) {
      setHiddenCount(0);
      let hidden_count = 0;
      clonedDatasets.forEach((dataset) => {
        for (let i = 0; i < dataset.length; i++) {
          if (hiddenIndexes.includes(i)) {
            // update the value at hidden indexes to zero,
            // so that it do not get displayed as well not counted in counts
            const hiddenLabel = hiddenLabels.find(
              (item: any) => item['index'] === i,
            );
            if (
              hiddenLabel !== undefined &&
              severityFilters.find(
                (item: any) => item === hiddenLabel['label'],
              ) !== undefined
            ) {
              hidden_count += dataset[i];
            }
            dataset[i] = 0;
          }
        }
      });
      setHiddenCount(hidden_count);
    } else if (chartType === ChartType.Bar && hiddenIndexes.length > 0) {
      setHiddenIndexes([]);
    }
    // calculate the total count
    if (total_count !== undefined) {
      if (hiddenIndexes.length === 0) {
        setHiddenCount(0);
      }
      setTotalCount(total_count - hiddenCount);
    } else {
      let count = 0;
      clonedDatasets.forEach((dataset) => {
        count += dataset.reduce((partialSum, x) => partialSum + x, 0);
      });
      setTotalCount(count);
    }

    // add the All datasets to the chart
    if (operationType === 'Average') {
      const clonedCountDatasets = _.cloneDeep(countDatasets);
      let updatedDatasets =
        clonedDatasets.length > 1
          ? [avgArrays(clonedDatasets, clonedCountDatasets), ...clonedDatasets]
          : clonedDatasets;
      return updatedDatasets;
    } else {
      let updatedDatasets =
        clonedDatasets.length > 1
          ? [mergeArrays(clonedDatasets), ...clonedDatasets]
          : clonedDatasets;
      return updatedDatasets;
    }
  }, [
    chartType,
    datasets,
    hiddenIndexes,
    countDatasets,
    severityFilters,
    hiddenCount,
  ]);

  const calculatedTooltipLabels: string[] = useMemo(() => {
    return datasets.length > 1 ? ['All', ...tooltipLabels] : tooltipLabels;
  }, [datasets, tooltipLabels]);

  useEffect(() => {
    if (redirectUrl) history.push(redirectUrl);
  }, [redirectUrl]);

  // custom legend on click handler
  useEffect(() => {
    const chart =
      chartType === ChartType.Bar
        ? barChartReference?.current?.chartInstance
        : pieChartReference?.current?.chartInstance;
    if (chartType === ChartType.Doughnut && chart) {
      const defaultLegendClickHandler = function (e, legendItem) {
        var index = legendItem.index;
        var i, ilen, meta;

        for (i = 0, ilen = (chart.data.datasets || []).length; i < ilen; ++i) {
          meta = chart.getDatasetMeta(i);
          // toggle visibility of index if exists
          if (meta.data[index]) {
            meta.data[index].hidden = !meta.data[index].hidden;
          }
        }

        chart.update();
      };
      chart.options.legend.onClick = (e, legendItem) => {
        if (legendItem['hidden'] === false) {
          let labelsarray: any = hiddenLabels;
          labelsarray.push({
            index: legendItem['index'],
            label: legendItem['text'],
          });
          setHiddenLabels(labelsarray);
        }
        if (legendItem['hidden'] === true) {
          let labelsarray: any = hiddenLabels;
          labelsarray = labelsarray.filter((e) => e !== legendItem['text']);
          setHiddenLabels(labelsarray);
        }

        const index: number = legendItem.index;
        const hidden = !legendItem.hidden; // clicked to toggle state

        const otherHiddenLegendItems = chart.legend.legendItems.filter(
          (legendItem) =>
            legendItem.hidden === true && legendItem.index !== index,
        );
        const currentHiddenLegendItems = hidden
          ? [...otherHiddenLegendItems, legendItem]
          : otherHiddenLegendItems;

        setHiddenIndexes(
          currentHiddenLegendItems.map((legendItem) => legendItem.index),
        );
        // console.log({
        //     datasets,
        //     labels,
        //     e,
        //     legendItem,
        //     index: legendItem.index,
        //     chart,
        //     legendItems: chart.legend.legendItems,
        //     otherHiddenLegendItems,
        //     currentHiddenLegendItems,
        //     hiddenIndexes: currentHiddenLegendItems.map(legendItem => legendItem.index),
        // });

        defaultLegendClickHandler(e, legendItem);
      };
    }

    // Custom call back function for custom tooltips
    // use for vulnerability metrics charts
    if (chart) {
      if (tooltipsCallbackFunction && tooltipsTitleCallbackFunction) {
        chart.options.tooltips.callbacks = {
          label: tooltipsCallbackFunction,
          title: tooltipsTitleCallbackFunction,
        };
      } else if (tooltipsCallbackFunction) {
        chart.options.tooltips.callbacks = {
          label: tooltipsCallbackFunction,
        };
      }
      chart.update();
    }

    // yAxesLabel for bar charts
    if (yAxesLabel && chartType === ChartType.Bar && chart) {
      chart.options.scales.yAxes[0].scaleLabel = {
        display: true,
        labelString: yAxesLabel,
      };
      chart.update();
    }
  }, [
    chartType,
    datasets,
    labels,
    tooltipsCallbackFunction,
    tooltipsTitleCallbackFunction,
    yAxesLabel,
  ]);

  function getLabels() {
    return labels.map((label) => {
      if (label?.length > 50) {
        return `${label.substring(0, 50)}...`;
      }
      return label;
    });
  }

  const chartHeader = customChartHeader || (
    <CardHeader>
      <CardTitle tag="div" className="mb-0">
        <div className="d-flex flex-row align-items-baseline">
          <h1 className="d-inline mb-0">{totalCount}</h1>
          <h5 className="card-category px-1">{title}</h5>
        </div>
      </CardTitle>
    </CardHeader>
  );

  function handleChartClick(event, chart: ChartJs, labels: string[]): void {
    // Chart.js v3.9.1 with react-chartjs-2 v3.0.4,
    if (!chart) return;
    const activeElements = chart.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: true },
      false,
    );

    if (activeElements?.length > 0) {
      const activeElement = activeElements[0];
      const datasetIndex = activeElement.datasetIndex;
      const dataIndex = activeElement.index;

      const dataset = chart.data.datasets[datasetIndex];
      const label = labels[dataIndex];
      const sourceType = dataset.label;

      if (label) {
        const url = getDrillDownUrl(label, sourceType || '');
        if (url) setRedirectUrl(url);
      }
    }
  }
  return (
    <Card className="card-chart border border-light">
      {chartHeader}
      <CardBody style={{ cursor: 'pointer' }}>
        <div className="chart-area">
          {chartType === ChartType.Doughnut && (
            <Doughnut
              ref={(node) => {
                if (node != null) pieChartReference.current = node;
              }}
              data={getChartData(
                getLabels(),
                calculatedTooltipLabels,
                calculatedDatasets,
                severityFilters,
                colorMode,
              )}
              options={DOUGHNUT_CHART_OPTIONS}
              plugins={[DoughnutChartDataLabels]} // Use the explicitly typed plugin
              onClick={(event) =>
                handleChartClick(event, pieChartReference.current, getLabels())
              }
            />
          )}
          {chartType === ChartType.Bar && (
            <Bar
              ref={(node) => {
                if (node != null) barChartReference.current = node;
              }}
              data={getChartData(
                labels,
                calculatedTooltipLabels,
                calculatedDatasets,
                severityFilters,
                colorMode,
              )}
              options={BAR_CHART_OPTIONS}
              plugins={[ChartDataLabels]} // No need for explicit typing here
              onClick={(event) =>
                handleChartClick(event, barChartReference.current, labels)
              }
            />
          )}
          {chartType === ChartType.BarStacked && (
            <Bar
              ref={(node) => {
                if (node != null) barChartReference.current = node;
              }}
              data={getChartData(
                labels,
                tooltipLabels,
                datasets,
                severityFilters,
                colorMode,
                colors,
              )}
              options={STACKED_BAR_CHART_OPTIONS}
              plugins={[ChartDataLabels]} // No need for explicit typing here
              onClick={(event) =>
                handleChartClick(event, barChartReference.current, labels)
              }
            />
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default BarOrDoughnutChart;
